import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * Parent orchestrator — subscribes to LiveDashboardChannel, runs the
 * polling fallback state machine, forwards received payloads to the
 * live-feed and live-kpi child controllers via Stimulus outlets.
 *
 * Reads timing constants from data-*-value attributes (see
 * index.html.erb) so the JS never duplicates Ruby's
 * Reporting::Constants.
 */
// LRPO-5130 (rework) — last-resort floor for the snapshot abort deadline, used only
// if snapshotTimeoutMsValue is somehow absent or non-positive. Keep in step with the
// snapshotTimeoutMs value default below.
const SNAPSHOT_TIMEOUT_FALLBACK_MS = 10000

// LRPO-5130 (rework 4) — last-resort floor for the POLL CADENCE, for the same reason
// as the deadline above: an absent/NaN interval value reaches setInterval as
// `undefined`, which the platform coerces to 0ms — a tight loop hammering the most
// expensive endpoint on the page. The cadence must never be anything but a positive
// number of milliseconds.
const POLL_CADENCE_FALLBACK_MS = 30000

// LRPO-5130 (rework 4) — TRANSPORT BACKOFF. See _bookTransportFailure.
//
// A failing snapshot endpoint backs the poll off geometrically (1×, 2×, 4×, 8× the
// base cadence) instead of terminating the dashboard. Both bounds are load-bearing:
//   * the FACTOR is capped so the dashboard keeps probing and RECOVERS BY ITSELF the
//     moment the server does — an unbounded backoff is just a slower death;
//   * the absolute CEILING stops a long base cadence (the 60s idle rate) multiplied
//     by the factor from drifting into "effectively never".
const SNAPSHOT_BACKOFF_MAX_FACTOR = 8
const SNAPSHOT_BACKOFF_MAX_MS     = 300000 // 5 minutes

const REFRESH_LABEL = (seconds) => {
  if (seconds < 5)   return "Updated just now"
  if (seconds < 60)  return `Updated ${seconds}s ago`
  if (seconds < 300) return `Updated ${Math.round(seconds / 60)} min ago`
  return "Updated (stale) — refresh page"
}

export default class extends Controller {
  static targets = [
    "refreshIndicator",
    "staleDataBanner",
    "realtimeUnavailableBanner",
    "sessionExpiredBanner",
    "repActivity"
  ]
  static outlets = ["live-feed", "live-kpi"]
  static values  = {
    eventExhibitorId:          Number,
    snapshotUrl:               String,
    tz:                        { type: String, default: "UTC" },
    pollIntervalMs:            { type: Number, default: 30000 },
    pollIdleMs:                { type: Number, default: 60000 },
    refreshTickMs:             { type: Number, default: 15000 },
    fallbackCheckMs:           { type: Number, default: 5000 },
    fallbackNeverConnectedMs:  { type: Number, default: 10000 },
    maxFeedItems:              { type: Number, default: 100 },
    // LRPO-5130 (Part A) — trailing debounce for the immediate post-broadcast
    // snapshot poll. One reconciliation fetch fires ~1s after the LAST broadcast
    // so a burst of scans/edits collapses into a single fetch (storm protection).
    immediatePollDebounceMs:   { type: Number, default: 1000 },
    // LRPO-5130 (3rd QA re-open) — hard ceiling on how long a scan may sit in the
    // pending buffer waiting for its coherent update. A PURE trailing debounce
    // never fires at all while broadcasts keep arriving faster than the debounce
    // window (a door-scanning burst, a mobile offline sync), which would freeze the
    // feed for the entire burst. Once the OLDEST pending scan has waited this long
    // the delay collapses to 0 and we commit what we have — so the feed can never
    // be starved. See _scheduleCoherentUpdate.
    coherentMaxWaitMs:         { type: Number, default: 2500 },
    // LRPO-5130 (rework) — ABORT DEADLINE for the snapshot request.
    //
    // The snapshot is now the FEED's only delivery path, and it is single-flight.
    // A `fetch` has no implicit timeout, so a request that never settles (a worker
    // stuck on the snapshot's aggregates; a proxy holding the connection open; this
    // repo's own LockWaitTimeout history — LRPO-4965 / 5503) would never run its
    // `finally`, would never release _pollInFlight, and would freeze the entire
    // dashboard silently and permanently: no feed rows, no KPIs, no charts, and not
    // even the staleness banner or the circuit breaker, because neither can fire
    // from a promise that never settles.
    //
    // One deadline closes all of it. The abort rejects into the catch below, which
    // already flushes the held scans through the optimistic fallback and books the
    // failure; the existing `finally` then releases the latch. Well above the p99 of
    // an endpoint whose slow path is aggregates + a 100-row eager load + one
    // render_to_string, so it can only ever fire on a genuinely stuck request.
    snapshotTimeoutMs:         { type: Number, default: 10000 },
    // LRPO-5300 — how long the dashboard may go without a broadcast OR a
    // successful snapshot poll before we tell the user their numbers may be out
    // of date. See _evaluateStaleness for why the banner is bound to this and
    // NOT to the WebSocket's up/down state.
    //
    // MUST stay above TWO idle poll cycles (2 × pollIdleMs = 120s): with the
    // socket up on a quiet booth the poll is the only thing marking data fresh,
    // so a single missed poll pushes the next success out to 2 × pollIdleMs.
    // Anything lower banners a one-off blip. Mirrors
    // Reporting::Constants::STALE_DATA_MS (the real value arrives via
    // data-live-dashboard-stale-data-ms-value); this default is the fallback.
    staleDataMs:               { type: Number, default: 150000 }
  }

  connect() {
    this._lastRefreshAt        = new Date()
    this._pollTimer            = null
    this._activePollIntervalMs = null
    this._immediatePollTimer   = null
    this._webSocketConnected   = false
    // LRPO-5130 (rework 4) — TWO counters, because a slow server and an expired
    // session are not the same event and must not share a circuit breaker. See
    // _bookAuthFailure / _bookTransportFailure.
    this._authFailures         = 0
    this._transportFailures    = 0
    this._terminated           = false
    // LRPO-5130 (3rd QA re-open) — scans received from the cable but not yet
    // committed to the DOM. They are held until the snapshot lands so the feed row
    // and the metrics appear together (see _onScanReceived). _pendingSince stamps
    // the OLDEST held scan, which is what the max-wait ceiling is measured from.
    this._pendingScans         = []
    this._pendingSince         = null
    // LRPO-5130 (rework) — SINGLE-FLIGHT SNAPSHOT STATE.
    //
    // _poll() is async and is reachable from THREE places (the idle/fallback
    // setInterval, the debounced coherent update, and _onConnected's reconcile).
    // Without a guard, two fetches could be in flight at once, and the one that
    // STARTED LATER could RESOLVE FIRST — applying older server truth over newer
    // and making the KPIs and the feed visibly go backwards. Worse, a scan that
    // arrived while a request was in flight was reconciled against that request's
    // snapshot, which was built BEFORE the scan committed: the client then decided
    // the snapshot didn't cover it and prepended the row on top of pre-scan KPIs —
    // re-creating, exactly, the "the feed updates first, the metrics lag" defect
    // this ticket exists to fix.
    //
    //   _pollInFlight       — one snapshot request at a time, ever.
    //   _pollRerunRequested — a poll requested during a flight is not dropped; it
    //                         is coalesced into ONE trailing re-run after the
    //                         in-flight response has been applied.
    //   _pollStartedAt      — when the in-flight request left the browser. Anything
    //                         buffered at/after this instant CANNOT be assumed to be
    //                         in the response and stays pending (see
    //                         _reconcilePendingScans).
    //   _pollSeq / _lastAppliedPollSeq — monotonic tokens; a response older than the
    //                         last applied snapshot is discarded, never applied.
    this._pollInFlight         = false
    this._pollStartedAt        = null
    this._pollRerunRequested   = false
    this._pollSeq              = 0
    this._lastAppliedPollSeq   = 0
    // Turbo Drive connects and disconnects this controller on every visit, and
    // `await`ing anything (see _onConnected) means a continuation can land after
    // teardown. Every async continuation captures this token and bails if it no
    // longer matches, so a dead instance can never re-arm a timer that the live
    // instance has no handle for and therefore could never clear.
    this._generation = Symbol("live-dashboard")

    this._subscription = consumer.subscriptions.create(
      { channel: "LiveDashboardChannel", event_exhibitor_id: this.eventExhibitorIdValue },
      {
        connected:    this._onConnected.bind(this),
        disconnected: this._onDisconnected.bind(this),
        received:     this._onScanReceived.bind(this),
        rejected:     this._onRejected.bind(this)
      }
    )

    this._refreshTimer = setInterval(() => this._tick(), this.refreshTickMsValue)

    // LRPO-5300 — both failsafes now keep their handles so disconnect() can
    // cancel them. Previously the handles were dropped on the floor: on a Turbo
    // visit the torn-down instance's 5s timer still fired _startPolling(), and
    // the setInterval it created was unreachable by any live _stopPolling — so
    // every visit to /exhibitor/live left another orphan poller hammering the
    // snapshot endpoint for the rest of the browsing session.
    this._fallbackCheckTimer = setTimeout(() => {
      this._fallbackCheckTimer = null
      if (!this._webSocketConnected) this._applyPollCadence()
    }, this.fallbackCheckMsValue)

    this._neverConnectedTimer = setTimeout(() => {
      this._neverConnectedTimer = null
      // Engineer-facing only. A cable that never comes up is not a user-facing
      // event while the polling fallback keeps the data fresh — the banner is
      // driven by staleness, not by the transport (see _evaluateStaleness).
      if (!this._webSocketConnected) {
        console.warn("LiveDashboard: ActionCable never connected — running on the polling fallback.")
      }
    }, this.fallbackNeverConnectedMsValue)
  }

  disconnect() {
    this._generation = null
    this._subscription?.unsubscribe()
    clearInterval(this._refreshTimer)
    this._stopPolling()
    // LRPO-5130 — drop any held scans. This controller is being torn down (Turbo
    // visit), so there is nothing left to render them into; the fresh instance
    // reconciles from its own first snapshot.
    this._pendingScans = []
    this._pendingSince = null
    // LRPO-5130 (rework) — an in-flight fetch outlives this teardown. Its
    // continuation checks _generation (nulled above) and bails, but clear the
    // trailing re-run flag too so nothing re-arms behind us.
    this._pollRerunRequested = false
    // LRPO-5130 (Part A) — cancel any pending debounced immediate poll so a
    // disconnect (navigation/teardown) never fires a late fetch on a dead
    // controller.
    this._cancelImmediatePoll()
    // LRPO-5300 — the two connect() failsafes must die with the controller too.
    this._clearFailsafeTimers()
  }

  // LRPO-5300 — the debounced reconcile poll (_scheduleCoherentUpdate) is the ONE
  // path that calls _poll() directly instead of going through _startPolling, so
  // the terminal guard in that choke point cannot reach it. Every state that
  // ends this controller's right to fetch — teardown (disconnect) AND session
  // expiry (_enterSessionExpired) — must therefore cancel it by hand, or a
  // reconcile armed by the last broadcast still fires ~1s later against a dead
  // controller or a terminated session.
  _cancelImmediatePoll() {
    if (this._immediatePollTimer) {
      clearTimeout(this._immediatePollTimer)
      this._immediatePollTimer = null
    }
  }

  // LRPO-5300 — the connect() failsafes are one-shot, but until they fire they
  // are live references to a controller that may already be dead (Turbo
  // teardown) or terminal (session expired). Both of those states cancel them.
  _clearFailsafeTimers() {
    if (this._fallbackCheckTimer) {
      clearTimeout(this._fallbackCheckTimer)
      this._fallbackCheckTimer = null
    }
    if (this._neverConnectedTimer) {
      clearTimeout(this._neverConnectedTimer)
      this._neverConnectedTimer = null
    }
  }

  // --- ActionCable handlers -----------------------------------------------

  async _onConnected() {
    this._webSocketConnected = true
    const generation = this._generation
    // Reconcile missed scans before downshifting polling.
    await this._poll()
    // LRPO-5300 — the await above is a suspension point: a Turbo navigation
    // (teardown) or a fresh drop can land while the fetch is in flight. Arming
    // the idle interval then would attach a setInterval to a controller nobody
    // holds a handle to any more.
    if (this._generation !== generation || !this._webSocketConnected) return
    this._applyPollCadence()
  }

  // LRPO-5300 — a socket drop is the POLLER's problem, not the user's.
  //
  // The previous 6s "grace window" before alarming was inert by construction:
  // ActionCable's ConnectionMonitor refuses to even ATTEMPT a reopen until 6s
  // have passed since the drop (disconnectedRecently vs staleThreshold=6), then
  // waits reopenDelay=500ms, then needs a handshake + welcome + re-subscribe +
  // confirm_subscription before `connected` fires. The banner therefore won that
  // race 100% of the time, and no fixed window could ever have won it — the
  // monitor's own backoff escalates to 30s between attempts on a flap.
  //
  // So: downshift to the faster fallback cadence IMMEDIATELY (delaying that only
  // made the data staler) and say nothing. The banner is bound to whether the
  // DATA is actually stale (see _evaluateStaleness) — a blip the 30s poll covers
  // is invisible to the user, and a dashboard that has genuinely stopped
  // refreshing still warns them honestly.
  _onDisconnected(data) {
    this._webSocketConnected = false

    // A close with `reconnect: false` (ApplicationCable::Connection's
    // reject_unauthorized_connection is the path that matters here) STOPS the
    // ConnectionMonitor — ActionCable will never retry. Promising a reconnect
    // that cannot happen is what left a permanent "falling back to 30-second
    // refresh" banner on screen. Say what is true instead: the session is over.
    if (data && data.willAttemptReconnect === false) {
      this._enterSessionExpired()
      return
    }

    console.warn("LiveDashboard: real-time connection dropped — polling fallback engaged.")
    this._applyPollCadence()
  }

  // LRPO-5300 — a REJECTED subscription is an authorization outcome, not a
  // network one: LiveDashboardChannel#subscribed rejects an unknown
  // EventExhibitor or a user with no ExhibitorUserLinkage for it, and the socket
  // stays perfectly healthy throughout. Rendering that as "Real-time connection
  // dropped" (which is what the old bare console.warn plus the never-connected
  // failsafe did between them) sent QA hunting a network fault that did not
  // exist.
  //
  // The snapshot poll authenticates separately over HTTP and generally still
  // works, so keep it running: the dashboard stays fresh, it just isn't live.
  _onRejected() {
    console.warn("LiveDashboardChannel subscription rejected — real-time updates unavailable; polling fallback engaged.")
    this._webSocketConnected = false
    if (this.hasRealtimeUnavailableBannerTarget) {
      this.realtimeUnavailableBannerTarget.classList.remove("d-none")
    }
    this._applyPollCadence()
  }

  // *** LRPO-5130 (3rd QA re-open) — THE COHERENT COMMIT ***
  //
  // QA: "Metrics and Live Scan Feed data are not updated simultaneously. The Live
  // Scan Feed updates first, followed by the metrics."
  //
  // They were right, and it was structural. A scan used to be applied through TWO
  // independent paths at TWO different times:
  //
  //   t=0     the cable payload → prependScan() + the optimistic KPI bumps
  //   t≈1s+   the debounced snapshot poll → Leads/Hr, the charts, Scanner
  //           Activity, Target, the this-hour delta, and the server-truth counts
  //
  // So the feed row visibly landed a beat before the KPIs it belongs to, and for
  // that beat the dashboard showed a scan the numbers didn't know about. No amount
  // of adding fields to the optimistic path fixes that — the lagging metrics are
  // SERVER AGGREGATES (active scanners, hourly velocity, prorated rate); the client
  // cannot derive them without either a round-trip or a pile of duplicated,
  // drift-prone arithmetic.
  //
  // So we invert it: HOLD the scan instead of racing it. The payload goes into a
  // pending buffer, the debounced snapshot is armed, and when it lands
  // _applySnapshot writes the feed AND every metric from ONE server object in ONE
  // synchronous pass (see _applySnapshot). Feed and metrics can no longer disagree,
  // because they are now the same update. The cost is that the row appears with the
  // metrics (~1s) rather than ~1s ahead of them — which is exactly what QA asked
  // for, and the snapshot's recent_scans already contains the scan (the broadcast
  // fires after_create_commit, so the row is committed before the frame is even
  // sent).
  //
  // The optimistic path is NOT deleted — it is now the FALLBACK. If the snapshot
  // fetch fails, _flushPendingScans replays these payloads through it, so a broken
  // snapshot endpoint degrades to the old behaviour instead of losing scans.
  _onScanReceived(payload) {
    // A lead EDIT (qualifier/response/attendee/qualifier-rename/username change)
    // broadcasts a {refresh:true} no-op signal, NOT a feed scan. There is no row to
    // hold and no KPI to bump — an edit creates no lead — so just arm the same
    // coherent update and let server truth land.
    if (payload && payload.refresh === true) {
      // LRPO-5367 — a lead-data edit while the feed is PAUSED gets no feed row
      // (correct — it isn't a new scan) and the paused feed won't repopulate from
      // the poll, so it would otherwise be invisible. Flag it on the feed so its
      // banner surfaces "updates" (the pending-changes indicator). The stashed
      // snapshot from the poll below lets a click reconcile the changed rows.
      if (this.hasLiveFeedOutlet && this.liveFeedOutlet.isPaused) {
        this.liveFeedOutlet.markPausedEdit()
      }
      this._notePendingUpdate()
      this._scheduleCoherentUpdate()
      return
    }

    // *** LRPO-5515 — A BULK FRAME IS JUST N SCANS. ***
    //
    // A mobile OFFLINE SYNC (POST /api/v4/bulk_leads -> Leads::BulkLeadCreator) now
    // coalesces server-side into ONE frame carrying the whole batch — { scans: [...] }
    // — instead of one frame per scan. Unwrap it here and let every entry take
    // EXACTLY the path a per-scan frame would have taken below, IN ORDER: the feed
    // dedups by lead_id (LRPO-5242), so a same-lead re-scan later in the batch still
    // wins the row, and the rendered result is identical to N separate frames.
    //
    // Unwrapping at the top (rather than as its own early-returning branch) is what
    // keeps the bulk path inside the coherent commit: these scans are HELD and land
    // with the metrics that describe them, like every other scan. Left as a raw
    // frame it would have been buffered as a single lead-less "scan" and prepended
    // as one garbage row — silently losing every scan of the sync.
    //
    // No optimistic KPI bump, deliberately: the bulk path never threads new_lead, so
    // _applyScanOptimistically's new_lead gate already leaves Total/Today/This-Hour
    // to the server-truth snapshot, exactly as when these arrived as N frames.
    const scans = (payload && Array.isArray(payload.scans)) ? payload.scans : [payload]

    // PAUSED FEED — the coherent commit is MOOT here, so don't apply it.
    //
    // While the feed is paused the list is frozen by design, so there is no visible
    // feed update to be out of step with the metrics: the skew QA reported cannot
    // occur. Hand the scans straight to the feed controller exactly as before (it
    // buffers the rows and raises its "N new scans" banner) and just arm the metrics
    // update.
    //
    // This is also the conservative choice. The paused path owns the most intricate
    // state in this feature — the buffer, the persisted-pause restore, the
    // _seenLeadIds baseline and the snapshot-vs-snapshot diff (LRPO-5135 / 5363 /
    // 5367 / 5368). Holding scans back from it would make the FIRST paused snapshot
    // absorb a not-yet-counted scan into its baseline and silently drop it from the
    // banner. Leave that machinery exactly as it is.
    if (this.hasLiveFeedOutlet && this.liveFeedOutlet.isPaused) {
      scans.forEach(scan => this.liveFeedOutlet.prependScan(scan))
      this._notePendingUpdate()
      this._scheduleCoherentUpdate()
      return
    }

    // Hold the scans for the coherent commit.
    //
    // bufferedAt is load-bearing, not bookkeeping: it is how _reconcilePendingScans
    // knows whether a given snapshot could POSSIBLY have known about this scan. A
    // scan buffered while a request was already in flight is NOT covered by that
    // request's answer (the server built it earlier), so it must survive the
    // reconcile rather than being force-rendered against stale KPIs.
    const bufferedAt = Date.now()
    scans.forEach(scan => this._pendingScans.push({ scan, bufferedAt }))
    this._notePendingUpdate()
    this._scheduleCoherentUpdate()
  }

  // LRPO-5363 (code review) — the live-feed controller woke up in a page the SERVER
  // rendered FROZEN at a pause mark, while the feed's own UI says it is live (a URL
  // carrying ?paused_at was opened in a tab that never paused — sessionStorage, and
  // therefore the pause, is per-tab). The feed has already stripped the mark so the
  // next load is clean, but the stale list is on screen NOW and nothing else would
  // replace it: the interval poller only ever starts when the cable FAILS
  // (_onDisconnected / _onRejected / the never-connected failsafe), so on a healthy
  // socket and a quiet booth the frozen rows would sit there for the life of the
  // page view. Reconcile from the server instead of hoping for a poll.
  //
  // Wired on the dashboard root (live_dashboard/index.html.erb) via
  // data-action="live-feed:staleFeed->live-dashboard#refreshFeedNow" — the feed's
  // dispatch() bubbles up to this controller's element.
  //
  // *** MERGE NOTE (LRPO-5363 × LRPO-5130 rework 4) ***
  //
  // This used to call _scheduleImmediatePoll(), which LRPO-5130 renamed to
  // _scheduleCoherentUpdate() and gave a mandatory companion, _notePendingUpdate().
  // Both are needed, and for a reason — not just to satisfy the rename:
  //
  //   * _notePendingUpdate() stamps _pendingSince, the origin the max-wait ceiling is
  //     measured from. Without it a refresh requested on a BUSY booth would be re-armed
  //     by every arriving scan's own _scheduleCoherentUpdate() and could be starved
  //     past the ceiling indefinitely — i.e. the stale feed would keep NOT healing
  //     precisely when the booth is busiest. Noting it bounds the wait like any other
  //     pending update. (It needs no entry in _pendingScans; _pendingSince only feeds
  //     the debounce arithmetic and is cleared the moment a fetch is dispatched.)
  //
  //   * _scheduleCoherentUpdate() deliberately does NOT bypass 5130's transport gate.
  //     While the snapshot endpoint is failing (_transportFailures > 0) the burst path
  //     stops requesting entirely and the backed-off INTERVAL owns the recovery. That
  //     is the right answer here too: a stale feed cannot be healed without a server
  //     snapshot, so there is nothing to gain by driving another request into a sick
  //     endpoint — and doing so would re-create the very "burst drives a failing
  //     transport" defect 5130 fixed. The interval keeps polling on its backoff clock
  //     (_bookTransportFailure -> _applyPollCadence -> _startPolling), so the moment
  //     the transport recovers, _poll -> _applySnapshot -> replaceFeed heals the feed.
  //     Until then the stale-data banner is up and telling the truth.
  refreshFeedNow() {
    this._notePendingUpdate()
    this._scheduleCoherentUpdate()
  }

  // _pendingSince stamps the arrival of the oldest update request that NO snapshot
  // request is yet servicing. It is the origin the max-wait ceiling is measured
  // from, and it is cleared the moment a reconciliation fetch is dispatched for the
  // work held so far (see _scheduleCoherentUpdate's timer and _poll) — NOT when
  // that fetch resolves.
  //
  // That distinction is the whole ballgame. Clearing it on RESOLVE meant `waited`
  // kept growing for the entire duration of an in-flight request, which pinned the
  // debounce delay at 0, so every further broadcast fired ANOTHER snapshot fetch:
  // storm protection was gone in exactly the burst the ceiling was written for.
  _notePendingUpdate() {
    if (this._pendingSince == null) this._pendingSince = Date.now()
  }

  // Trailing debounce (storm protection: a burst of N broadcasts collapses into
  // ONE snapshot fetch) WITH a max-wait ceiling.
  //
  // The ceiling is not optional. A pure trailing debounce re-arms on every
  // broadcast, so a stream arriving faster than immediatePollDebounceMs never
  // fires at all — and now that the feed is committed BY the poll, "never fires"
  // would mean "the feed never updates during a busy burst", which is precisely
  // when the Live Command Center matters most. Once the oldest UN-SERVICED update
  // has waited coherentMaxWaitMs the delay collapses to 0 and we commit.
  _scheduleCoherentUpdate() {
    // LRPO-5300 — a terminated session may not ARM new work either, not just be
    // stopped from continuing it (see _cancelImmediatePoll).
    if (this._terminated) return
    if (this._immediatePollTimer) clearTimeout(this._immediatePollTimer)

    const waited = this._pendingSince == null ? 0 : Date.now() - this._pendingSince
    const delay  = Math.max(
      0,
      Math.min(this.immediatePollDebounceMsValue, this.coherentMaxWaitMsValue - waited)
    )

    this._immediatePollTimer = setTimeout(() => {
      this._immediatePollTimer = null
      // A reconciliation fetch is being DISPATCHED for everything held so far, so
      // the ceiling clock restarts here. (If _poll finds a request already in
      // flight it coalesces into that request's trailing re-run — either way the
      // work now has a fetch behind it.) Updates that arrive from here on stamp a
      // FRESH _pendingSince and get their own full debounce window, which is what
      // keeps a burst from spraying one fetch per broadcast.
      this._pendingSince = null

      // *** LRPO-5130 (rework 4) — DO NOT LET A BURST DRIVE A FAILING TRANSPORT. ***
      //
      // This is the path that turned a slow server into a dead dashboard. While the
      // snapshot endpoint is failing, every arriving scan still armed this timer, so
      // the burst kept firing fresh requests — each one bounded by its own 10s
      // deadline, each abort immediately followed by the trailing re-run — and three
      // of those in a row hit the circuit breaker in ~30 seconds flat.
      //
      // A backed-off poll must actually be backed off, so while the transport is
      // failing the burst path stops requesting entirely: the held scans are
      // delivered the pre-5130 way (straight from their cable payloads —
      // unsynchronised, but the feed keeps moving), and the backed-off INTERVAL owns
      // the recovery. One probe per backoff window, not one per scan.
      if (this._transportFailures > 0) {
        this._flushPendingScans()
        return
      }

      this._poll()
    }, delay)
  }

  // FALLBACK ONLY — the pre-5130 optimistic path, kept for when the snapshot fetch
  // cannot land. Renders the held scans straight from their cable payloads and
  // bumps the DISTINCT-keyed KPIs, so a failing snapshot endpoint degrades to the
  // old (unsynchronised but functional) behaviour rather than swallowing scans.
  _flushPendingScans() {
    const queued = this._pendingScans.splice(0)
    this._pendingSince = null
    queued.forEach(entry => this._applyScanOptimistically(entry.scan))
  }

  _applyScanOptimistically(scan) {
    if (this.hasLiveFeedOutlet) this.liveFeedOutlet.prependScan(scan)

    // LRPO-5242 — only bump the DISTINCT-keyed KPIs (Total/Today/This-Hour) when
    // this scan created a genuinely NEW lead. The server counts these per DISTINCT
    // attendee/lead, so a rescan of the same lead must NOT advance them — otherwise
    // the live numbers over-count until the poll reconciles. Broadcasts that don't
    // thread new_lead (import/door paths) are treated as not-new and left to the
    // poll, never over-counting.
    if (!(scan && scan.new_lead === true && this.hasLiveKpiOutlet)) return

    this.liveKpiOutlet.animateDelta("totalLeads",    1)
    const nextThisHour = this.liveKpiOutlet.animateDelta("leadsThisHour", 1)
    this.liveKpiOutlet.animateDelta("todayTotal",    1)
    // LRPO-5135 — the goal "current leads" count is total_leads, so bump it in
    // lockstep with the totalLeads card (otherwise it lagged a poll behind).
    this.liveKpiOutlet.animateDelta("goalCount",     1)
    this.liveKpiOutlet.updateGoalProgress()
    // Keep the "This Hour vs last hour" delta arrow in lock-step with the
    // leadsThisHour bump. No-ops before the first snapshot has cached a
    // previous-hour figure.
    this.liveKpiOutlet.bumpThisHourDeltaOptimistic(nextThisHour)
  }

  // --- Polling fallback + circuit breaker ---------------------------------

  _startPolling(intervalMs) {
    // LRPO-5300 — the choke point for every INTERVAL poller: once the session is
    // terminal, nothing may resurrect one — in particular not the still-pending
    // connect() failsafe, which outlives the drop that terminated us. Polling a
    // dead session is pure load that can never succeed.
    //
    // NOTE this guard does NOT cover the debounced reconcile: that one calls
    // _poll() directly and never comes through here. _enterSessionExpired /
    // disconnect cancel it explicitly via _cancelImmediatePoll.
    if (this._terminated) return
    // LRPO-5300 — re-arming an interval restarts its countdown from zero, and a
    // flapping socket calls this on every disconnect. Left unguarded, a cable
    // flapping faster than the poll cadence would starve the poll of ever firing
    // — the exact scenario in which the fallback matters most. No-op when the
    // requested cadence is already the one running.
    if (this._pollTimer && this._activePollIntervalMs === intervalMs) return
    // Unconditional clear before reassignment prevents the two-handles
    // race when connect/disconnect events interleave.
    this._stopPolling()
    this._activePollIntervalMs = intervalMs
    this._pollTimer = setInterval(() => this._poll(), intervalMs)
  }

  _stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer)
      this._pollTimer = null
    }
    this._activePollIntervalMs = null
  }

  // SINGLE-FLIGHT. Exactly one snapshot request may be in flight at a time, and its
  // response is applied only if it is still the freshest one we have seen.
  //
  // The snapshot endpoint is expensive (LiveDashboardReport's aggregates + a 100-row
  // eager-loaded recent-scans query + a render_to_string of the rep-activity
  // partial), and it is now the FEED's delivery path as well as the metrics', so
  // overlapping requests were both a load problem and a CORRECTNESS problem: two
  // in-flight fetches resolve in whatever order the network hands them back, and
  // _applySnapshot applies whichever lands LAST — so a staler snapshot could
  // overwrite a fresher one and the KPIs and the feed would visibly go backwards.
  async _poll() {
    // LRPO-5130 — with no snapshot URL there is no coherent update to wait for, so
    // held scans must still reach the DOM. (Only the unit specs hit this; the view
    // always renders the URL.)
    if (!this.snapshotUrlValue) {
      this._flushPendingScans()
      return
    }

    // A poll requested while one is in flight is COALESCED, not dropped: it becomes
    // a single trailing re-run once the in-flight response has been applied. That
    // keeps a burst (or an idle-interval tick colliding with the coherent update) to
    // one request at a time while never losing the reconciliation it asked for.
    if (this._pollInFlight) {
      this._pollRerunRequested = true
      return
    }

    const generation = this._generation
    const seq        = ++this._pollSeq
    const startedAt  = Date.now()

    this._pollInFlight  = true
    this._pollStartedAt = startedAt
    // This request services every update held so far — see _notePendingUpdate.
    this._pendingSince  = null

    // THE ABORT DEADLINE (see snapshotTimeoutMs). A hung request is the one failure
    // this state machine cannot survive: _pollInFlight is released ONLY in the
    // `finally` below, so a promise that never settles latches the dashboard shut
    // forever. Bound the wait, and a hang becomes an ordinary error — one the catch
    // already knows how to degrade gracefully.
    //
    // Fall back to the declared default if the value is missing or nonsensical: an
    // undefined timeout would collapse to setTimeout(fn, 0) and abort every request
    // the instant it left the browser.
    const timeoutMs = Number(this.snapshotTimeoutMsValue) > 0
      ? Number(this.snapshotTimeoutMsValue)
      : SNAPSHOT_TIMEOUT_FALLBACK_MS
    const abortController = new AbortController()
    const timeoutId       = setTimeout(() => abortController.abort(), timeoutMs)

    try {
      const resp = await fetch(this.snapshotUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
        signal: abortController.signal
      })
      if (!resp.ok) {
        // LRPO-5130 — the snapshot is now the FEED's delivery path too, so a failed
        // fetch must not swallow the scans it was carrying. Replay them through the
        // optimistic fallback: unsynchronised, but never lost.
        this._flushPendingScans()
        // LRPO-5130 (rework 4) — 401/403 is the ONLY status that means what the
        // session-expired banner says. A 500/502/504/429 is the SERVER having a bad
        // time; the user's session is fine. See _bookTransportFailure.
        if (resp.status === 401 || resp.status === 403) this._bookAuthFailure()
        else this._bookTransportFailure()
        return
      }

      this._authFailures = 0
      const data = await resp.json()

      // The two awaits above are suspension points. A Turbo teardown can land in
      // them (the generation token changes), and a response older than the last one
      // we applied must never overwrite it. Discard, don't apply.
      //
      // The generation guard must come BEFORE _bookTransportSuccess: that call
      // re-arms the poll interval, and re-arming one on a torn-down controller is
      // precisely the orphan-poller leak LRPO-5300 fixed.
      if (this._generation !== generation) return
      if (seq < this._lastAppliedPollSeq) return
      this._lastAppliedPollSeq = seq

      // A complete, parseable snapshot landed — the transport is healthy. Retire any
      // backoff and return the poll to its base cadence.
      this._bookTransportSuccess()

      // startedAt is passed EXPLICITLY: the reconcile needs the instant THIS request
      // left the browser, which is the only honest cut-off for "could the server have
      // known about this scan?".
      this._applySnapshot(data, startedAt)
      this._markDataFresh()
    } catch (e) {
      // A network error, a malformed body — or the abort deadline above. All three
      // mean the same thing to the dashboard: this snapshot is not coming, so the
      // scans it was carrying must be rendered from their cable payloads
      // (unsynchronised, but never lost) and the failure must be booked.
      //
      // NONE of them is an authentication outcome — see _bookTransportFailure.
      //
      // The generation guard is what the deadline makes necessary: a request hanging
      // at the moment of a Turbo teardown is now GUARANTEED to reject ~10s later, on
      // a controller with no DOM left to render into. Book nothing, render nothing,
      // and above all do not re-enter the state machine from a dead instance — the
      // fresh controller owns the page now and reconciles from its own snapshot.
      if (this._generation !== generation) return
      console.warn("LiveDashboard poll failed:", e)
      this._flushPendingScans()
      this._bookTransportFailure()
    } finally {
      // The deadline must never outlive its request: left armed, it would abort a
      // request that has already succeeded and book a failure against a perfectly
      // healthy dashboard.
      clearTimeout(timeoutId)

      this._pollInFlight  = false
      this._pollStartedAt = null

      const rerun = this._pollRerunRequested
      this._pollRerunRequested = false
      // One trailing re-run, and only into a controller that is still alive and not
      // terminal — otherwise a coalesced request could resurrect a dead session's
      // fetch loop.
      //
      // LRPO-5130 (rework 4) — and NOT into a transport that just failed. The re-run
      // fires SYNCHRONOUSLY off the previous request's settlement, so on a busy booth
      // (where a mid-flight scan always leaves one queued) a hanging endpoint used to
      // retry back-to-back at the deadline cadence: abort at 10s → re-run → abort at
      // 20s → re-run → abort at 30s → dashboard terminated. There is nothing urgent
      // left to re-run for anyway — the failure path already flushed the held scans
      // to the DOM — so let the backed-off interval do the retrying, on its own clock.
      const transportHealthy = this._transportFailures === 0
      if (rerun && transportHealthy && !this._terminated && this._generation === generation) this._poll()
    }
  }

  // --- Failure classification (LRPO-5130 rework 4) --------------------------
  //
  // *** A SLOW SERVER IS NOT AN EXPIRED SESSION. ***
  //
  // There used to be ONE counter. Any failed snapshot — a 500, a network blip, and
  // (once the abort deadline was added) a HANG — took the dashboard three strikes
  // closer to _enterSessionExpired(), which is TERMINAL: it unsubscribes the cable,
  // stops the poll, and puts up "Session expired — your authentication has timed
  // out", leaving a manual page reload as the only way back.
  //
  // On a busy booth those three strikes are not spaced by the poll interval at all.
  // Scans keep arriving, so the coherent-update timer keeps asking for a poll, and
  // the trailing re-run fires the next request the instant the previous one aborts.
  // Abort at 10s → re-run → abort at 20s → re-run → abort at 30s → TERMINAL. A
  // ~30-second slow patch on the snapshot endpoint — and this repo has a documented
  // LockWaitTimeout history (LRPO-4965 / 5503) — would permanently kill the Live
  // Command Center for every exhibitor watching it, and blame their authentication.
  //
  // So the two failures are told apart:
  //
  //   AUTH (401/403 on the snapshot, and the cable's willAttemptReconnect:false) —
  //     the session really is gone and no amount of retrying brings it back.
  //     Terminal, and the banner tells the truth. Kept at 3 strikes so a single
  //     freak response can't end a working session.
  //
  //   TRANSPORT (a hang/abort, a network error, a 5xx, a malformed body) — the
  //     server is struggling, or the network is. The session is fine, the cable is
  //     very probably still up and still delivering scans, and the condition is
  //     usually transient. Terminating here destroys a dashboard that would have
  //     recovered on its own. So: NEVER terminal. Back the poll off (stop hammering
  //     a server that is already in trouble), keep painting scans from their cable
  //     payloads via _flushPendingScans, and let the STALE-DATA banner say the
  //     honest thing — "Live updates have stalled — the figures below may be out of
  //     date. We're still retrying; reload the page if this persists." That banner
  //     raises itself (nothing marks the data fresh while the snapshot is failing)
  //     and clears itself the moment a snapshot lands again.
  _bookAuthFailure() {
    this._authFailures += 1
    if (this._authFailures >= 3) this._enterSessionExpired()
  }

  _bookTransportFailure() {
    this._transportFailures += 1
    this._applyPollCadence()
  }

  _bookTransportSuccess() {
    if (this._transportFailures === 0) return
    this._transportFailures = 0
    this._applyPollCadence()
  }

  // The cadence the poll SHOULD be running at right now, in ONE place — so a cable
  // event and a transport failure can never fight over the interval.
  //
  // Base rate: fast (pollIntervalMs) while the cable is down and the poll is the only
  // source of truth; slow (pollIdleMs) while the cable is up and the poll is just a
  // reconciliation backstop. Then stretched by the transport backoff, if any.
  _applyPollCadence() {
    this._startPolling(this._pollCadenceMs())
  }

  _pollCadenceMs() {
    const raw  = this._webSocketConnected ? this.pollIdleMsValue : this.pollIntervalMsValue
    const base = Number(raw) > 0 ? Number(raw) : POLL_CADENCE_FALLBACK_MS
    if (this._transportFailures <= 0) return base

    const factor = Math.min(2 ** (this._transportFailures - 1), SNAPSHOT_BACKOFF_MAX_FACTOR)
    return Math.min(base * factor, SNAPSHOT_BACKOFF_MAX_MS)
  }

  _enterSessionExpired() {
    // LRPO-5130 — terminal: no further snapshot will EVER land, so any scan still
    // held for a coherent commit has lost its delivery vehicle. Render it from the
    // cable payload before shutting down (and BEFORE _terminated is set, so
    // _flushPendingScans isn't fighting a guard). Better an unsynchronised row than
    // a scan the exhibitor never sees.
    this._flushPendingScans()

    this._terminated = true
    this._stopPolling()
    this._clearFailsafeTimers()
    // LRPO-5300 — a broadcast that landed moments before the session died may
    // have armed the ~1s debounced reconcile. It calls _poll() DIRECTLY, so
    // _startPolling's terminal guard never sees it; only this cancels it.
    // Without it, a terminated session still fires one last fetch — and if that
    // fetch is the one that 401s, it re-enters _enterSessionExpired.
    this._cancelImmediatePoll()
    this._subscription?.unsubscribe()
    // LRPO-5300 — this state is terminal, so retract the "your data is going
    // stale, we're still trying" story: only the reload prompt is true now.
    this._setStaleDataBanner(false)
    if (this.hasSessionExpiredBannerTarget) {
      this.sessionExpiredBannerTarget.classList.remove("d-none")
    }
  }

  // *** THE ONE COHERENT COMMIT (LRPO-5130, 3rd QA re-open) ***
  //
  // Everything below is written from a SINGLE server object inside ONE synchronous
  // JS task: the KPI cards, the scan feed, Scanner Activity and both charts. The
  // browser cannot paint between them, so the feed row and the metrics that
  // describe it necessarily land in the same frame — which is what QA asked for
  // ("Metrics and Live Scan Feed ... refreshed at the same time").
  //
  // Do NOT reintroduce an await, a setTimeout or a rAF between these calls, and do
  // NOT move any of them back onto the cable path: either would re-open the
  // two-timeline skew this ticket has now bounced three times for.
  //
  // `pollStartedAt` is the instant the request that produced `data` left the
  // browser. It defaults to Infinity for the callers that have no request behind
  // them (the unit specs drive _applySnapshot directly), which means "treat this
  // snapshot as covering everything currently held".
  _applySnapshot(data, pollStartedAt = Infinity) {
    if (this.hasLiveKpiOutlet) this.liveKpiOutlet.applyServerValues(data)
    // LRPO-5135 — KPIs and rep-activity keep updating on every poll, but the
    // feed LIST is frozen while the user has paused it. Skip the feed swap when
    // paused so scans that arrived during the pause stay buffered instead of
    // being repopulated into the live list (replaceFeed also self-guards while
    // paused; this is the explicit single-point skip the fix plan calls for).
    if (
      this.hasLiveFeedOutlet &&
      Array.isArray(data.recent_scans) &&
      !this.liveFeedOutlet.isPaused
    ) {
      this.liveFeedOutlet.replaceFeed(data.recent_scans)
    } else if (
      // LRPO-5367 — feed is frozen while paused, so we don't render, but hand it
      // the fresh snapshot so a click on the "updates" banner can reconcile the
      // edited rows on demand (the feed stores it, no render).
      this.hasLiveFeedOutlet &&
      Array.isArray(data.recent_scans) &&
      this.liveFeedOutlet.isPaused
    ) {
      this.liveFeedOutlet.receivePausedSnapshot(data.recent_scans)
    }
    this._applyRepActivity(data)
    this._applyCharts(data)
    // Still inside the same synchronous task — see the banner comment above.
    this._reconcilePendingScans(data, pollStartedAt)
  }

  // LRPO-5130 — retire the held scans now that the snapshot has been applied.
  //
  // Normally the snapshot IS their delivery: the broadcast fires after_create_commit,
  // so every held scan is already committed and comes back in recent_scans, and the
  // replaceFeed above has just rendered it. Dropping the buffer is then all that is
  // left to do.
  //
  // But recent_scans is a WINDOW — today only (event tz), newest 100
  // (LiveDashboardController#snapshot). A held scan the window doesn't carry would
  // silently vanish if we cleared the buffer blindly, which would be a far worse bug
  // than the one we are fixing. So anything the snapshot did NOT account for is
  // rendered from its own cable payload instead. This runs AFTER replaceFeed
  // (a rebuild would otherwise wipe it) and while paused prependScan buffers it,
  // exactly as before.
  //
  // Payloads with no lead_id (legacy/import broadcasts) can't be matched against the
  // window, so they count as uncovered — but see _shouldReplayUncovered: "uncovered"
  // is a necessary condition for replaying a scan, never a sufficient one.
  //
  // *** THE IN-FLIGHT CUT-OFF (LRPO-5130 rework) ***
  //
  // A snapshot can only answer for scans that had committed BEFORE the server built
  // it, and the earliest it can have built it is when we sent the request. So a scan
  // buffered at/after `pollStartedAt` is NOT this snapshot's business, whatever its
  // recent_scans window says:
  //
  //   t=0.0  scan A arrives, arms the coherent update for t=1.0
  //   t=1.0  the request leaves; the server builds the snapshot ~t=1.05 (A only)
  //   t=1.1  scan B arrives and is buffered — the request is still in flight
  //   t=1.3  the response lands: it contains A, and knows nothing of B
  //
  // Treating B as an "uncovered window miss" here and prepending it would put B's row
  // on screen against KPIs that pre-date B — a feed row the metrics know nothing
  // about, which is VERBATIM the defect QA reopened this ticket for. So B stays
  // pending and rides the next snapshot, which carries its row AND its numbers
  // together. The genuine window-miss guard (a scan outside today/the newest 100)
  // still applies to everything the request could actually have seen.
  _reconcilePendingScans(data, pollStartedAt = Infinity) {
    if (!this._pendingScans.length) return

    const scans = Array.isArray(data.recent_scans) ? data.recent_scans : []
    const covered = new Set(
      scans.map(s => s && s.lead_id).filter(id => id != null)
    )

    const settled      = []
    const stillPending = []
    this._pendingScans.forEach(entry => {
      if (entry.bufferedAt < pollStartedAt) settled.push(entry)
      else stillPending.push(entry)
    })

    this._pendingScans = stillPending
    if (!stillPending.length) this._pendingSince = null

    // The window's ceiling and its capacity — computed ONCE for the whole batch, from
    // the snapshot the feed was just rebuilt from. See _shouldReplayUncovered.
    const windowNewestAt  = this._windowNewestAt(scans)
    const windowSaturated = scans.length >= this._maxFeedItems()

    settled
      .filter(entry => !(entry.scan && entry.scan.lead_id != null && covered.has(entry.scan.lead_id)))
      .filter(entry => this._shouldReplayUncovered(entry.scan, windowNewestAt, windowSaturated))
      .forEach(entry => {
        if (this.hasLiveFeedOutlet) this.liveFeedOutlet.prependScan(entry.scan)
      })

    // Belt-and-braces: a scan held over from an in-flight request always has a timer
    // (it armed one on arrival) or a coalesced re-run behind it. If it somehow has
    // neither, arm one — a held scan with nothing to deliver it would be invisible
    // until the next idle poll.
    if (stillPending.length && !this._immediatePollTimer && !this._pollRerunRequested) {
      this._scheduleCoherentUpdate()
    }
  }

  // *** LRPO-5130 (rework 4) — WHEN MAY AN UNCOVERED SCAN BE PREPENDED? ***
  //
  // liveFeedOutlet.prependScan is a STRICT PREPEND — live_feed_controller's
  // _insertOrReplace inserts before listTarget.firstChild — and the feed then
  // _trim()s the list back down to maxItems. So a prepend is only ever CORRECT for a
  // scan that genuinely belongs at the TOP of the feed: one NEWER than everything the
  // snapshot just rendered. Replaying every uncovered scan unconditionally is not a
  // safety net, it is corruption.
  //
  // IT BREAKS EXACTLY WHERE IT MATTERS. recent_scans is a WINDOW: today only, newest
  // 100 (Exhibitor::LiveDashboardController#snapshot — .order(RECENCY_DESC).limit(100)).
  // A mobile offline sync of 150 scans arrives as ONE coalesced frame (LRPO-5515) and
  // is held in full, but the snapshot answers with the newest 100 — so the OLDEST 50
  // of the batch come back "uncovered". Prepending them stacked the 50 OLDEST scans
  // of the sync ON TOP of the 100 newest, and _trim then evicted 50 rows the server
  // had actually sent: an INVERTED feed that silently dropped rows.
  //
  // This is the same defect LRPO-5367 fixed once already on the PAUSED path (see
  // live_feed_controller's "_seenLeadIds is the baseline, not the DOM" comment: "it
  // flushed those 90 OLDER scans through the prepending _insertOrReplace path,
  // landing them ABOVE the 10 genuinely-newest rows: an inverted feed"). It must not
  // be re-created on the unpaused path.
  //
  // THE WINDOW'S CEILING IS THE WHOLE TEST. recent_scans is RECENCY_DESC, so scans[0]
  // is the newest row on screen:
  //
  //   * scan NEWER than the window's newest → server truth has not caught up with it
  //     (a replica/commit race). It really IS the top of the feed. PREPEND — this is
  //     the case the uncovered fallback exists for, and the only one it is sound for.
  //
  //   * scan OLDER than the window's newest → the server DELIBERATELY excluded it: it
  //     is not among the newest 100 (or not in today's range). The feed's contract is
  //     "the newest N scans", so this is not a row the feed was ever going to show.
  //     DROP it. Prepending could only put a stale row above fresher ones and evict a
  //     real one. Nothing is lost that was ever visible: the scan is committed, it is
  //     counted in every server aggregate on the page (Total / Today / This Hour /
  //     the charts all come from the same snapshot), it is simply below the feed's
  //     cut — exactly as it would be after a plain page reload.
  //
  // With no usable timestamps to compare (legacy/import payloads with no `timestamp`,
  // or an empty window), fall back to CAPACITY: a saturated window cannot carry
  // another row, so prepending into it can only corrupt; an unsaturated one has space
  // and no established ordering to break.
  _shouldReplayUncovered(scan, windowNewestAt, windowSaturated) {
    const scanAt = this._scanTimeMs(scan)
    if (scanAt != null && windowNewestAt != null) return scanAt >= windowNewestAt
    return !windowSaturated
  }

  // The NEWEST instant the snapshot's window carries. recent_scans is ordered
  // RECENCY_DESC by the server, so that is the first entry — but scan forward for the
  // first PARSEABLE timestamp so one malformed row can't blind the guard.
  _windowNewestAt(scans) {
    for (const scan of scans) {
      const at = this._scanTimeMs(scan)
      if (at != null) return at
    }
    return null
  }

  // Both the cable payload (LeadScan#live_dashboard_payload) and the poll's window
  // (Exhibitor::LiveDashboardController#serialize_scan) carry `timestamp` as an
  // ISO8601 string of the same recency key the window is ORDERED by
  // (COALESCE(last_scan_at, created_at)) — so the two are directly comparable.
  _scanTimeMs(scan) {
    if (!scan || scan.timestamp == null) return null
    const at = Date.parse(scan.timestamp)
    return Number.isNaN(at) ? null : at
  }

  // The feed's OWN cap is the authority here — it is what _trim() actually enforces —
  // so read it off the outlet and only fall back to this controller's declared value
  // when there is no feed to ask.
  _maxFeedItems() {
    if (this.hasLiveFeedOutlet && Number(this.liveFeedOutlet.maxItemsValue) > 0) {
      return Number(this.liveFeedOutlet.maxItemsValue)
    }
    return Number(this.maxFeedItemsValue) > 0 ? Number(this.maxFeedItemsValue) : 100
  }

  // LRPO-5130 (Part C) — re-render the live Chartkick charts in place from the
  // snapshot's two series so the Hourly Velocity column chart and the
  // Today-vs-Yesterday overlay refresh on every poll instead of waiting for a
  // full page reload. Chartkick keeps a global registry of live charts keyed by
  // element id (window.Chartkick.charts[id]); calling updateData(series) swaps
  // the data and re-renders without destroying the canvas.
  //
  // LRPO-5130 (rework) — the EMPTY-STATE caveat noted here previously is fixed:
  // the chart partials now ALWAYS render the underlying canvas (registering it
  // in window.Chartkick.charts from the first paint), so a chart that starts
  // the day empty still receives this update the moment the first scan's
  // reconciliation poll lands. See _updateChart's reveal step below.
  _applyCharts(data) {
    const reg = window.Chartkick && window.Chartkick.charts
    if (!reg) return
    this._updateChart(reg, "live-hourly-velocity", data.hourly_velocity_today)
    this._updateChart(reg, "live-today-vs-yesterday", data.today_vs_yesterday)
  }

  _updateChart(reg, id, series) {
    const chart = reg[id]
    if (!chart || series == null) return
    try {
      chart.updateData(series)
      // LRPO-5130 (rework) — the Hourly Velocity chart starts hidden behind a
      // "no scans yet" overlay when the day begins empty (see
      // _hourly_velocity.html.erb). Reveal it the moment updateData lands a
      // real, positive value. No-op for charts without a companion overlay
      // element (e.g. Today-vs-Yesterday, whose empty state is a legitimate
      // longer-lived "no data from yesterday" condition, not this transient
      // first-scan case) — the querySelector below simply finds nothing.
      if (this._seriesHasData(series)) this._revealChart(id)
    } catch (e) {
      console.warn(`LiveDashboard chart updateData failed for ${id}:`, e)
    }
  }

  // Handles both Chartkick series shapes used on this page: a plain
  // { label: count } hash (Hourly Velocity) and an array of named series
  // ({ name, data: { label: count } }, the Today-vs-Yesterday overlay).
  _seriesHasData(series) {
    const hasPositiveValue = (obj) => Object.values(obj || {}).some((v) => Number(v) > 0)
    if (Array.isArray(series)) return series.some((s) => hasPositiveValue(s && s.data))
    return hasPositiveValue(series)
  }

  _revealChart(id) {
    document.querySelector(`[data-chart-empty-state="${id}"]`)?.classList.add("d-none")
  }

  // LRPO-5155 — refresh the Scanner Activity status dots on every poll.
  // The snapshot carries `rep_activity_html` (the freshly-rendered <li> rows
  // with the server's recomputed active/idle/inactive status). We swap ONLY
  // the inner rows into the STABLE <ul aria-live="polite"> container (the
  // repActivity target). Swapping the CONTENTS of the stable live region —
  // rather than replacing the <ul> element itself — is what preserves screen-
  // reader announcements: recreating the live region each poll would reset it
  // and suppress the announcement of the status change. Guard the empty/absent
  // case so a payload without the key (e.g. an older server) is a no-op rather
  // than blanking the list.
  //
  // CHANGE-DETECTION GUARD (a11y): only write innerHTML when the html actually
  // differs from the last value we applied. Without it, every 60s poll mutates
  // the aria-live region even when nothing changed, and some screen readers
  // (NVDA/JAWS) re-announce the entire unchanged scanner list each minute —
  // precisely the noise a polite live region is meant to avoid. Caching the
  // last-applied string and short-circuiting on equality means the live region
  // mutates (and announces) ONLY when a scanner's status actually changes.
  // _lastRepActivityHtml starts undefined, so the FIRST poll after the
  // server-rendered initial paint does one harmless swap to seed the cache;
  // every steady-state poll thereafter is a no-op until something changes.
  _applyRepActivity(data) {
    if (!this.hasRepActivityTarget) return
    if (typeof data.rep_activity_html !== "string") return
    if (data.rep_activity_html === this._lastRepActivityHtml) return
    // XSS note: rep_activity_html is TRUSTED, same-origin HTML rendered by our
    // own _rep_activity_list.html.erb. Every interpolated value (scanner name,
    // status) goes through ERB's default `<%= %>` auto-escaping server-side
    // (the status is a fixed :active/:idle/:inactive symbol; the name is
    // HTML-escaped), so the markup is already safe by the time it reaches the
    // client — exactly like a Turbo Stream `replace` payload. innerHTML on the
    // STABLE live region (not element replacement) is required to preserve the
    // aria-live announcement; see _applySnapshot.
    this.repActivityTarget.innerHTML = data.rep_activity_html
    this._lastRepActivityHtml = data.rep_activity_html
  }

  // --- Data staleness -----------------------------------------------------

  // LRPO-5300 — the one place that records "the dashboard IS SHOWING current data".
  // It re-evaluates the banner immediately so a recovery clears it at once rather
  // than lingering until the next refresh tick.
  //
  // *** LRPO-5130 (rework) — ONLY A LANDED SNAPSHOT MAY CALL THIS. ***
  //
  // It used to be called from _onScanReceived as well, on the theory that "a
  // broadcast landed" means the data is current. The coherent commit made that
  // false: an arriving scan now renders NOTHING — it buffers a payload and arms a
  // snapshot. Marking the data fresh there claimed the screen was up to date at the
  // exact moment it demonstrably was not.
  //
  // And it was self-concealing. _evaluateStaleness measures from _lastRefreshAt, so
  // every arriving scan reset the staleness clock — which meant a dashboard frozen
  // behind a snapshot that had stopped landing stayed SILENT for as long as scans
  // kept arriving. The busier the booth, the more reliably the alarm was disarmed by
  // the very traffic that made it matter.
  //
  // So freshness now belongs to the coherent commit and nothing else: _poll() marks
  // it after _applySnapshot, the one instant the claim is true. Note the fallback
  // path (_flushPendingScans, when the snapshot could not land) deliberately does
  // NOT mark it either — those rows come from cable payloads, not server truth, and
  // the aggregate figures the banner speaks for ("the FIGURES below may be out of
  // date") really have stopped refreshing.
  _markDataFresh() {
    this._lastRefreshAt = new Date()
    this._evaluateStaleness()
  }

  // The banner answers the only question the user actually has — "are these
  // numbers current?" — instead of reporting on the transport, which they can do
  // nothing about and which the polling fallback routinely covers. A WebSocket
  // drop with a healthy poll is silent; data that has genuinely stopped
  // refreshing warns, whatever the socket says.
  _evaluateStaleness() {
    if (this._terminated) return
    const stale = (new Date() - this._lastRefreshAt) >= this.staleDataMsValue
    this._setStaleDataBanner(stale)
  }

  _setStaleDataBanner(show) {
    if (!this.hasStaleDataBannerTarget) return
    this.staleDataBannerTarget.classList.toggle("d-none", !show)
  }

  _tick() {
    this._tickRefreshIndicator()
    this._evaluateStaleness()
  }

  _tickRefreshIndicator() {
    if (!this.hasRefreshIndicatorTarget) return
    const seconds = Math.round((new Date() - this._lastRefreshAt) / 1000)
    this.refreshIndicatorTarget.textContent = REFRESH_LABEL(seconds)
  }
}
